import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { ServicePage } from "@/components/site/ServicePage";
import heroImg from "@/assets/portfolio-3.jpg";
import heroImgWebp from "@/assets/portfolio-3.webp";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";
import { contenido } from "@/content/pages/stands-para-ferias";
import { vistaServicio } from "@/lib/page-content/vista";
import type { ServiceSchemaData } from "@/lib/seo-schema-apply";
import { datosHeadServicio, jsonLdServicio } from "@/lib/jsonld-servicios";

const DEFAULT_TITLE = "Stands para Ferias en Lima: Diseño y Alquiler | Improve";
const DEFAULT_DESCRIPTION =
  "Stands para ferias y exposiciones en Lima y todo el Perú: a medida, modulares y portátiles. Diseño, producción, montaje y desmontaje, o alquiler por evento.";

// Imagen provisional (del sitio) hasta asignar una propia al slot «servicio-stands-para-ferias» desde el panel.
const props = {
  slug: "/stands-para-ferias" as const,
  heroImg,
  heroImgWebp,
  heroSrcset:
    "/assets/portfolio-3-400w.webp 400w, /assets/portfolio-3-680w.webp 680w, /assets/portfolio-3-800w.webp 800w",
};
const relatedSlugs = ["/anfitrionas", "/merchandising-corporativo", "/activaciones-btl"] as const;

function StandsParaFeriasPage() {
  const { siteImageSlots, pageContent } = Route.useRouteContext();
  const vista = vistaServicio(contenido, pageContent);
  const slot = siteImageSlots?.["servicio-stands-para-ferias"];
  return (
    <PageShell>
      <ServicePage
        {...props}
        vista={vista}
        heroImg={slot?.src ?? props.heroImg}
        heroImgWebp={slot?.src ?? props.heroImgWebp}
        heroSrcset={slot?.srcSet ?? props.heroSrcset}
        relatedSlugs={[...relatedSlugs]}
      />
    </PageShell>
  );
}

export const Route = createFileRoute("/stands-para-ferias")({
  component: StandsParaFeriasPage,
  // El head() solo lee lo que arma el loader: el loader va en el chunk de la ruta junto con el
  // contenido, y así el contenido no entra al chunk de entrada de todas las páginas.
  loader: ({ context }) => datosHeadServicio(vistaServicio(contenido, context.pageContent)),
  head: ({ match, loaderData }) => {
    const override = match.context.seoOverride;
    const slot = match.context.siteImageSlots?.["servicio-stands-para-ferias"];
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/stands-para-ferias",
          image: override?.ogImage ?? slot?.src ?? heroImg,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: [
        ...canonicalLink(override?.canonicalPath ?? "/stands-para-ferias"),
        {
          rel: "preload",
          as: "image",
          href: slot?.src ?? heroImgWebp,
          type: "image/webp",
          fetchPriority: "high",
        },
      ],
      scripts: loaderData
        ? jsonLdServicio(
            loaderData,
            "/stands-para-ferias",
            slot?.src ?? heroImg,
            override?.schemaType === "service"
              ? (override.schemaData as Partial<ServiceSchemaData>)
              : null,
          )
        : [],
    };
  },
});
